/**
 * PhoneRequestPanel.tsx — 전화로 받은 팀장님 요청을 일일 업무 한 줄에서 등록(2026-10-25)
 *  지도사 「거기서 요청하기 안 누르고 나한테 전화로 요청하는 경우도 많아 · 그 경우 내가 등록하겠지」
 *  팀장님(동명이인은 소속 · 번호 끝자리로) → 거래처(그 팀장님 거래처) → 종류 → 내용 → 등록
 *  등록하면 fphub 「임지도사에게 요청」과 같은 정식 업무가 생기고 그 팀장님 고객관리 「맡긴 일」에 바로 보인다.
 *  휴대폰 「오늘 할 일」(펼친 줄)과 PC 일일 업무 상세가 같이 쓴다 · 서버 = routes/tm-clientdesk.js · services/phoneRequest.js
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { req } from '../api';

interface FpPick { id: string; name: string; title: string; company: string; tail: string; clients: number; fphub: boolean }
interface CoPick { id: string; name: string; ceo: string; biz_no: string }
interface Done { fp: string; company: string; kind_label: string; task_type: string; task_status: string }
interface Guess { request: Done | null; fps?: FpPick[]; kind?: string; subject?: string; kinds?: Record<string, string> }

const KINDS: [string, string][] = [['cert', '인증 갱신'], ['grant', '지원금'], ['registry', '등기'], ['tax', '세무 상담'], ['loan', '정책자금'], ['other', '기타']];
const NAVY = '#1B3A5C';
const INK = '#191F28';
const SUB = '#4E5968';
const LINE = '#D1D6DB';
const coShort = (s: string) => s.replace(/주식회사|유한회사|\(주\)|㈜/g, '').replace(/\s+/g, '').trim();

export default function PhoneRequestPanel({ todoId, title, onClose, onDone }: { todoId: string; title: string; onClose: () => void; onDone?: (msg: string) => void }) {
  const [g, setG] = useState<Guess | null>(null);
  const [err, setErr] = useState('');
  const [fps, setFps] = useState<FpPick[]>([]);
  const [fpQ, setFpQ] = useState('');
  const [fp, setFp] = useState<FpPick | null>(null);
  const [cos, setCos] = useState<CoPick[] | null>(null);
  const [coQ, setCoQ] = useState('');
  const [co, setCo] = useState<CoPick | null>(null);
  const [kind, setKind] = useState('other');
  const [subject, setSubject] = useState('');
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Done | null>(null);

  // 적은 글로 짐작 — 팀장님이 한 분이면 바로 고름
  useEffect(() => {
    let alive = true;
    req<Guess>('GET', `/client-desk/phone-request/guess?todo_id=${encodeURIComponent(todoId)}`).then((r) => {
      if (!alive) return;
      if (!r.success) { setErr(r.error || '불러오지 못했어요'); return; }
      setG(r.data);
      if (r.data.request) { setDone(r.data.request); return; }
      setFps(r.data.fps || []);
      if ((r.data.fps || []).length === 1) setFp((r.data.fps || [])[0]);
      setKind(r.data.kind || 'other');
      setSubject(r.data.subject || '');
    }).catch(() => { if (alive) setErr('불러오지 못했어요'); });
    return () => { alive = false; };
  }, [todoId]);

  // 팀장님을 고르면 그 팀장님 거래처 — 적은 글에 상호가 있으면 미리 고름
  useEffect(() => {
    let alive = true;
    setCos(null); setCo(null);
    if (!fp) return undefined;
    req<CoPick[]>('GET', `/client-desk/phone-request/companies?fp_id=${encodeURIComponent(fp.id)}&q=${encodeURIComponent(coQ)}`).then((r) => {
      if (!alive) return;
      const xs = r.success ? r.data : [];
      setCos(xs);
      const t = title.replace(/\s+/g, '');
      const hit = xs.filter((c) => coShort(c.name).length >= 2 && t.includes(coShort(c.name)));
      if (hit.length === 1) setCo(hit[0]);
      else if (xs.length === 1 && !coQ) setCo(xs[0]);
    }).catch(() => { if (alive) setCos([]); });
    return () => { alive = false; };
  }, [fp, coQ, title]);

  async function findFp() {
    const q = fpQ.trim();
    if (q.length < 2) return;
    const r = await req<FpPick[]>('GET', `/client-desk/phone-request/fps?q=${encodeURIComponent(q)}`).catch(() => null);
    setFps(r && r.success ? r.data : []);
    setFp(r && r.success && r.data.length === 1 ? r.data[0] : null);
  }
  async function submit() {
    if (!fp || !co || busy) return;
    if (kind === 'loan' && !asked) { setErr('정책자금은 대표님이 직접 원하셨을 때만 등록해요'); return; }
    setBusy(true); setErr('');
    try {
      const r = await req<{ request: Done | null; duplicate: boolean }>('POST', '/client-desk/phone-request', { todo_id: todoId, fp_id: fp.id, company_id: co.id, kind, subject, ceo_asked: asked });
      if (!r.success) { setErr(r.error || '등록하지 못했어요'); return; }
      if (r.data.request) setDone(r.data.request);
      if (onDone) onDone(`${fp.name} ${fp.title}님 「맡긴 일」에 올렸어요`);
    } catch { setErr('등록하지 못했어요. 잠시 뒤 다시 해 주세요'); }
    finally { setBusy(false); }
  }

  const box: CSSProperties = { display: 'grid', gap: 12, padding: 14, border: `1px solid ${LINE}`, borderRadius: 12, background: '#fff', minWidth: 0 };
  const lab: CSSProperties = { fontSize: 14, fontWeight: 700, color: SUB };
  const pick = (on: boolean): CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 48, padding: '6px 12px', borderRadius: 10, textAlign: 'left',
    border: on ? `2px solid ${NAVY}` : `1px solid ${LINE}`, background: on ? '#F1F4F8' : '#fff', color: INK, fontFamily: 'inherit', fontSize: 15, cursor: 'pointer', minWidth: 0 });
  const input: CSSProperties = { width: '100%', height: 44, padding: '0 12px', borderRadius: 10, border: `1px solid ${LINE}`, fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0 };
  const btn = (main: boolean): CSSProperties => ({ minHeight: 48, padding: '0 14px', borderRadius: 10, border: main ? 'none' : `1px solid ${LINE}`, background: main ? NAVY : '#fff', color: main ? '#fff' : INK, fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' });
  const clip: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
  const ready = !!fp && !!co && (kind !== 'loan' || asked);
  const coList = useMemo(() => (cos || []).slice(0, 8), [cos]);

  if (done) {
    return (
      <div style={{ ...box, background: '#F3F7F3', borderColor: '#BFD8C2' }} role="status">
        <b style={{ fontSize: 15, color: '#1F5A26' }}>팀장님 요청으로 등록했어요</b>
        <span style={{ fontSize: 14, color: INK, lineHeight: 1.6 }}>{done.fp}, {done.company}<br />{done.task_type}{done.task_status ? ` (${done.task_status === '진행' ? '진행 중' : done.task_status})` : ''}</span>
        <span style={{ fontSize: 13, color: SUB }}>팀장님 고객관리 「맡긴 일」에 보여요. 단계는 업무에서 체크해 주세요.</span>
        <button type="button" onClick={onClose} style={btn(false)}>닫기</button>
      </div>
    );
  }
  if (!g) return <div style={box}><span style={{ fontSize: 14, color: SUB }}>{err || '불러오는 중…'}</span>{err && <button type="button" onClick={onClose} style={btn(false)}>닫기</button>}</div>;

  return (
    <div style={box}>
      <b style={{ fontSize: 16, color: INK }}>팀장님 요청으로 등록</b>
      {/* 1) 팀장님 */}
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={lab}>팀장님</span>
        {fps.map((f) => (
          // 동명이인은 끝번호 · 소속으로 가른다 — 두 줄(이름 · 거래처 수 / 끝번호 · 소속)
          <button key={f.id} type="button" onClick={() => setFp(f)} aria-pressed={fp?.id === f.id} style={{ ...pick(fp?.id === f.id), display: 'grid', gap: 2, padding: '8px 12px' }}>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}><b style={clip}>{f.name} {f.title}</b><span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 13, color: SUB }}>거래처 {f.clients}곳</span></span>
            <span style={{ ...clip, color: SUB, fontSize: 13 }}>{[f.tail ? `끝번호 ${f.tail}` : '', f.company].filter(Boolean).join(', ') || '소속 없음'}</span>
          </button>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8 }}>
          <input value={fpQ} onChange={(e) => setFpQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !(e.nativeEvent as KeyboardEvent).isComposing) void findFp(); }}
            placeholder={fps.length ? '다른 팀장님 이름' : '팀장님 이름'} aria-label="팀장님 이름으로 찾기" style={input} />
          <button type="button" onClick={() => void findFp()} style={btn(false)}>찾기</button>
        </div>
      </div>
      {/* 2) 거래처 */}
      {fp && (
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={lab}>거래처</span>
          {co ? (
            <button type="button" onClick={() => setCo(null)} style={pick(true)} aria-label={`${co.name}, 다시 고르기`}>
              <b style={clip}>{co.name}</b><span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 13, color: SUB }}>바꾸기</span>
            </button>
          ) : (
            <>
              <input value={coQ} onChange={(e) => setCoQ(e.target.value)} placeholder="상호, 대표 이름, 사업자번호" aria-label="거래처 찾기" style={input} />
              {cos === null ? <span style={{ fontSize: 14, color: SUB }}>찾는 중…</span>
                : !coList.length ? <span style={{ fontSize: 14, color: SUB, lineHeight: 1.6 }}>{coQ ? '찾는 거래처가 없어요.' : '이 팀장님과 이어진 거래처가 없어요. 거래처를 먼저 이어 주세요.'}</span>
                  : coList.map((c) => (
                    <button key={c.id} type="button" onClick={() => setCo(c)} style={pick(false)}>
                      <b style={clip}>{c.name}</b><span style={{ ...clip, marginLeft: 'auto', flexShrink: 0, maxWidth: '40%', fontSize: 13, color: SUB }}>{c.ceo ? `대표 ${c.ceo}` : c.biz_no}</span>
                    </button>
                  ))}
            </>
          )}
        </div>
      )}
      {/* 3) 종류 · 4) 내용 */}
      {fp && co && (
        <>
          <div style={{ display: 'grid', gap: 6 }}>
            <span style={lab}>종류</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6 }} role="radiogroup" aria-label="요청 종류">
              {KINDS.map(([k, l]) => (
                <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
                  style={{ ...pick(kind === k), justifyContent: 'center', minHeight: 44, padding: '0 6px', fontWeight: kind === k ? 700 : 500, whiteSpace: 'nowrap' }}>{l}</button>
              ))}
            </div>
            {kind === 'loan' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, fontSize: 15, color: INK }}>
                <input type="checkbox" checked={asked} onChange={(e) => setAsked(e.target.checked)} style={{ width: 22, height: 22 }} />대표님이 직접 원하셨어요
              </label>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <span style={lab}>내용</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} placeholder="예: 가지급금 정리" aria-label="요청 내용" style={input} />
          </div>
        </>
      )}
      {err && <span role="alert" style={{ fontSize: 14, color: '#B42318', fontWeight: 600 }}>{err}</span>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button type="button" onClick={onClose} style={btn(false)}>그만두기</button>
        <button type="button" className="pr-submit" onClick={() => void submit()} disabled={!ready || busy} aria-disabled={!ready || busy}
          style={{ ...btn(true), opacity: ready && !busy ? 1 : 0.45, cursor: ready && !busy ? 'pointer' : 'default' }}>{busy ? '등록하는 중…' : '등록'}</button>
      </div>
    </div>
  );
}
